import { useSyncExternalStore } from "react";

/**
 * Evento nativo do navegador (Chrome/Edge/Android) disparado quando o site é instalável como
 * PWA. Não existe no Safari/iOS (a instalação é sempre manual).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type ResultadoInstalacao = "aceito" | "recusado" | "manual";

/**
 * O navegador dispara `beforeinstallprompt` uma vez, logo depois de carregar a página. Quando o
 * hook escutava só ao montar a tela da Biblioteca, o aviso podia chegar antes e se perdia: o botão
 * "às vezes aparecia, às vezes não". Por isso o aviso é guardado aqui, fora do React, desde o
 * primeiro carregamento (`iniciarCapturaInstalacaoPwa()` roda em main.tsx), e qualquer tela lê o
 * estado atual.
 */
let eventoGuardado: BeforeInstallPromptEvent | null = null;
let instaladoNestaSessao = false;
let iniciado = false;
const ouvintes = new Set<() => void>();

function avisarOuvintes() {
  ouvintes.forEach((ouvinte) => ouvinte());
}

/** `true` quando o Elos está aberto como app instalado (Android, desktop e Tela de Início do iPhone). */
function abertoComoApp(): boolean {
  if (typeof window === "undefined") return false;
  const modosDeApp = ["standalone", "fullscreen", "minimal-ui"];
  const porDisplayMode = modosDeApp.some(
    (modo) => window.matchMedia(`(display-mode: ${modo})`).matches,
  );
  const porSafariIos = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return porDisplayMode || porSafariIos;
}

export function iniciarCapturaInstalacaoPwa() {
  if (iniciado || typeof window === "undefined") return;
  iniciado = true;

  window.addEventListener("beforeinstallprompt", (e: Event) => {
    e.preventDefault();
    eventoGuardado = e as BeforeInstallPromptEvent;
    avisarOuvintes();
  });
  window.addEventListener("appinstalled", () => {
    instaladoNestaSessao = true;
    eventoGuardado = null;
    avisarOuvintes();
  });
  // Instalar pelo menu do navegador abre o app numa janela nova, mas a aba antiga pode continuar
  // aberta: quando o modo de exibição muda, o botão se atualiza.
  window.matchMedia("(display-mode: standalone)").addEventListener("change", avisarOuvintes);
}

function assinar(ouvinte: () => void) {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

/** Estado resumido num número (estável entre leituras): 1 = mostrar o botão, 2 = instala com um toque. */
function lerEstado(): number {
  const mostrar = !instaladoNestaSessao && !abertoComoApp();
  return (mostrar ? 1 : 0) | (mostrar && eventoGuardado ? 2 : 0);
}

/**
 * Estado do botão "Instalar app" do menu da conta.
 *
 * - `mostrarInstalar`: o Elos está no navegador (não é o app instalado). O botão aparece sempre
 *   que isto é verdadeiro, mesmo sem o aviso do navegador.
 * - `instalaComUmToque`: o navegador mandou o aviso; `instalar()` abre a janela de instalação.
 *   Sem o aviso, `instalar()` devolve "manual" e a tela mostra o passo a passo.
 */
export function usePwaInstall() {
  iniciarCapturaInstalacaoPwa();
  const estado = useSyncExternalStore(assinar, lerEstado, () => 0);

  async function instalar(): Promise<ResultadoInstalacao> {
    const evento = eventoGuardado;
    if (!evento) return "manual";
    // O aviso só pode ser usado uma vez.
    eventoGuardado = null;
    avisarOuvintes();
    await evento.prompt();
    const { outcome } = await evento.userChoice;
    return outcome === "accepted" ? "aceito" : "recusado";
  }

  return {
    mostrarInstalar: (estado & 1) === 1,
    instalaComUmToque: (estado & 2) === 2,
    instalar,
  };
}
